import { Download } from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { FormTextField } from "components/atoms";
import { ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DialogHelper } from "helpers";
import { usePaginationRequest } from "hooks";
import { IDownloadQRCodeBankRequest } from "model";
import { useState } from "react";
import { MasterDataApiService, DownloadApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";

interface IExportProductQRCodeBank {
  numberToExport: number;
  description: string;
  type: EExportType;
  email: string;
}

enum EExportType {
  CSV,
  EXCEL,
}

const validateSchema = yup.object().shape({
  numberToExport: yup
    .number()
    .min(1, "Số lượng phải lớn hơn 1")
    .required(translations.yupError.required),
  description: yup.string(),
  email: yup.string().email("Email không hợp lệ").required(translations.yupError.required),
});
const showDialog = () => {
    DialogHelper.open({
      title: "Thông báo",
      message: "Yêu cầu của bạn đã được tiếp nhận và đang được xử lý, sau khi hoàn tất bạn sẽ nhận được thông báo qua email."
    });
  };

const ProductQrCodeBank = () => {
  const [request] = useState({});
  const [refresh, setRefresh] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const {
    loading,
    result: { data },
  } = usePaginationRequest<any, any>(
    request,
    MasterDataApiService.getAvailableQRCodeBank,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const formik = useFormik<IExportProductQRCodeBank>({
    initialValues: {
      numberToExport: 0,
      description: "",
      type: EExportType.CSV,
      email: "",
    },
    validationSchema: validateSchema,
    onSubmit: (
      values: IExportProductQRCodeBank,
      { validateForm }: FormikHelpers<IExportProductQRCodeBank>
    ) => {
      validateForm().then(() => {
        setDownloading(true);
        const params: IDownloadQRCodeBankRequest = {
          numberToExport: values.numberToExport,
          descriptions: values.description,
          disableDownload: true,
          email: values.email,
        };
        if (values.type === EExportType.CSV) {
          DownloadApiService.downloadQRCodeBankCsv({
                ...params,
                isCsv: true,
              })
            .then((response) => {
              if (response) {
                setDownloading(false);
                showDialog();
              }
            })
            .catch((error) => {
              setDownloading(false);
              DialogHelper.open({
                title: "Error",
                message: "An error occurred while downloading the data."
              });
            });
        } else {
          DownloadApiService.downloadQRCodeBankXlsx(params)
            .then((response) => {
              if (response) {
                setDownloading(false);
                showDialog();
              }
            })
            .catch((error) => {
              setDownloading(false);
              DialogHelper.open({
                title: "Error",
                message: "An error occurred while downloading the data."
              });
            });
        }
      });
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.qrProductBank)}
      formik={formik}
      loading={downloading || loading}
      confirmButtonIcon={<Download />}
      confirmButtonText={"Export"}
    >
      <Grid
        container
        xs={3}
        item
        direction={"column"}
        sx={{ paddingRight: "10px" }}
      >
        <Box
          sx={{
            flex: 1,
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={"bold"}>
            {I18nTranslator(translations.receive.availableQuantity)}
          </Typography>
          <Typography fontWeight={"bold"}>
            {loading ? (
              <Skeleton variant="rectangular" width={140} height={20} />
            ) : (
              data?.availableQty.toLocaleString()
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={"bold"}>
            {I18nTranslator(translations.receive.exportedQuantity)}
          </Typography>
          <Typography fontWeight={"bold"}>
            {loading ? (
              <Skeleton variant="rectangular" width={120} height={20} />
            ) : (
              data?.exportedQty.toLocaleString()
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={"bold"}>
            {I18nTranslator(translations.receive.notExportedQuantity)}
          </Typography>
          <Typography fontWeight={"bold"}>
            {loading ? (
              <Skeleton variant="rectangular" width={120} height={20} />
            ) : (
              data?.notExportedQty.toLocaleString()
            )}
          </Typography>
        </Box>
      </Grid>
      <Grid container xs={9} item rowSpacing={2}>
        <Grid xs={12} container item columnSpacing={2}>
          <Grid xs={2} item>
            <FormTextField
              id="numberToExport"
              label={I18nTranslator(translations.receive.exportQuantity)}
              placeholder={I18nTranslator(translations.placeholder.code)}
              type={"number"}
            />
          </Grid>
          <Grid xs={4} item>
            <FormTextField
              id="email"
              label={I18nTranslator(translations.masterData.email)}
              placeholder={I18nTranslator(translations.placeholder.email)}
              type={"email"}
            />
          </Grid>
          <Grid container xs={4} item>
            <FormGroup sx={{ flexDirection: "row" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.type === EExportType.CSV}
                    onChange={() =>
                      formik.setFieldValue("type", EExportType.CSV)
                    }
                  />
                }
                label={".CSV"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.type === EExportType.EXCEL}
                    onChange={() =>
                      formik.setFieldValue("type", EExportType.EXCEL)
                    }
                  />
                }
                label={".EXCEL"}
              />
            </FormGroup>
          </Grid>
        </Grid>

        <Grid xs={12} item>
          <FormTextField
            id="description"
            label={I18nTranslator(translations.report.description)}
            placeholder={I18nTranslator(
              translations.placeholder.qrCodeDescription
            )}
            multiline
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

export default ProductQrCodeBank;
